import { useState } from 'react'
import { parseAsInteger, parseAsString, parseAsStringLiteral, useQueryStates } from 'nuqs'
import { PackagePlus, Search } from 'lucide-react'
import { useItems, useWarehouses } from '@/api/warehouse'
import { ListFilter, ListFilters } from '@/components/list-filters'
import { resultCount } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { SearchInput } from '@/components/search-input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { useDebouncedValue } from '@/hooks/use-debounced-value'
import { useUrlSearchText } from '@/hooks/use-url-search-text'
import { CONDITION_OPTIONS } from './model'
import { ReceiveStockSheet } from './receive-stock-sheet'
import { StockTable, StockTableSkeleton } from './stock-table'

const CONDITIONS = ['all', 'new', 'open_box', 'scratch_dent', 'used'] as const
const STATUSES = ['active', 'depleted', 'removed', 'all'] as const
const STATUS_LABELS: Record<(typeof STATUSES)[number], string> = {
  active: 'In stock',
  depleted: 'Sold out',
  removed: 'Removed',
  all: 'All stock',
}

/** Everything we hold in our warehouse(s), newest received first. */
export function WarehouseStockPage() {
  const [search, setSearch] = useUrlSearchText()
  const debouncedSearch = useDebouncedValue(search, 250)
  const [filters, setFilters] = useQueryStates({
    condition: parseAsStringLiteral(CONDITIONS).withDefault('all'),
    status: parseAsStringLiteral(STATUSES).withDefault('active'),
    warehouse: parseAsString,
    page: parseAsInteger.withDefault(1),
  })
  const [receiving, setReceiving] = useState(false)
  const warehouses = useWarehouses().data ?? []
  const { data, isLoading } = useItems({
    search: debouncedSearch.trim() || undefined,
    condition: filters.condition === 'all' ? undefined : filters.condition,
    status: filters.status,
    warehouse: filters.warehouse ?? undefined,
    page: filters.page,
    limit: 50,
  })
  const activeFilters = [
    filters.condition !== 'all',
    filters.status !== 'active',
    filters.warehouse != null,
  ].filter(Boolean).length
  const pages = data?.pages ?? 1

  return (
    <div className="mx-auto max-w-7xl space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h1 className="text-2xl font-bold tracking-tight">Warehouse</h1>
        <Button onClick={() => setReceiving(true)}>
          <PackagePlus className="mr-2 h-4 w-4" />
          Receive stock
        </Button>
      </div>

      <ListFilters
        count={resultCount(data?.total, 'item')}
        active={activeFilters}
        onClear={() => void setFilters({ condition: null, status: null, warehouse: null, page: 1 })}
        search={
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <SearchInput
              placeholder="Search model, product, bay..."
              value={search}
              onChange={(event) => {
                setSearch(event.target.value)
                void setFilters({ page: 1 })
              }}
              className="pl-9"
            />
          </div>
        }
      >
        <ListFilter label="Condition">
          <Select
            value={filters.condition}
            onValueChange={(value) => void setFilters({ condition: value as (typeof CONDITIONS)[number], page: 1 })}
          >
            <SelectTrigger aria-label="Condition filter" className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All conditions</SelectItem>
              {CONDITION_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </ListFilter>
        <ListFilter label="Status">
          <Select
            value={filters.status}
            onValueChange={(value) => void setFilters({ status: value as (typeof STATUSES)[number], page: 1 })}
          >
            <SelectTrigger aria-label="Stock status filter" className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {STATUSES.map((status) => (
                <SelectItem key={status} value={status}>
                  {STATUS_LABELS[status]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </ListFilter>
        <ListFilter label="Warehouse">
          {warehouses.length > 1 && (
            <Select
              value={filters.warehouse ?? 'all'}
              onValueChange={(value) => void setFilters({ warehouse: value === 'all' ? null : value, page: 1 })}
            >
              <SelectTrigger aria-label="Warehouse filter" className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All warehouses</SelectItem>
                {warehouses.map((warehouse) => (
                  <SelectItem key={warehouse.uuid} value={warehouse.uuid}>
                    {warehouse.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </ListFilter>
      </ListFilters>

      {isLoading ? (
        <StockTableSkeleton />
      ) : data?.items.length ? (
        <StockTable items={data.items} showWarehouse={warehouses.length > 1} />
      ) : (
        <div className="py-12 text-center text-muted-foreground">
          {search || activeFilters ? 'No stock matches' : 'Nothing in the warehouse yet'}
        </div>
      )}

      {pages > 1 && (
        <div className="flex items-center justify-between px-4 py-3">
          <p className="text-sm text-muted-foreground">
            Page {data?.page ?? 1} of {pages}
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => void setFilters({ page: Math.max(1, filters.page - 1) })}
              disabled={filters.page <= 1}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => void setFilters({ page: filters.page + 1 })}
              disabled={filters.page >= pages}
            >
              Next
            </Button>
          </div>
        </div>
      )}

      <ReceiveStockSheet open={receiving} onOpenChange={setReceiving} />
    </div>
  )
}
